import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Wand2, Search, CheckCircle2 } from 'lucide-react';

/**
 * Endpoint discovery results dialog.
 */
export function DiscoveryDialog({ open, onOpenChange, discovering, result, onApply }) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Wand2 className="h-5 w-5 text-[#0E2A47]" /> Endpoint discovery
                        {result?.platform && (
                            <span className="text-muted-foreground font-normal">\u00b7 {result.platform}</span>
                        )}
                    </DialogTitle>
                    <DialogDescription>
                        Scans the portal HTML + JavaScript bundles for likely API endpoints.
                    </DialogDescription>
                </DialogHeader>
                {discovering ? (
                    <div className="p-8 text-center text-sm text-muted-foreground">
                        <Search className="h-6 w-6 mx-auto mb-2 animate-pulse" />Scanning\u2026
                    </div>
                ) : result ? (
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                            <Stat label="Page status" value={`HTTP ${result.page_status || '\u2014'}`} />
                            <Stat label="Login state" value={(result.logged_in_hint || 'unknown').replace(/_/g, ' ')} />
                            <Stat label="Forms" value={result.forms?.length || 0} />
                            <Stat label="Endpoints" value={result.candidate_endpoints?.length || 0} />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                                Candidate endpoints (ranked)
                            </Label>
                            <ScrollArea className="h-72 mt-2 rounded-lg border border-border">
                                {(result.candidate_endpoints || []).length === 0 ? (
                                    <div className="p-6 text-center text-sm text-muted-foreground">
                                        No endpoints found.
                                    </div>
                                ) : (
                                    <div className="divide-y divide-border">
                                        {(result.candidate_endpoints || []).map((c) => (
                                            <CandidateRow
                                                key={c.url}
                                                candidate={c}
                                                onApply={() => onApply(result.platform, c.url)}
                                            />
                                        ))}
                                    </div>
                                )}
                            </ScrollArea>
                        </div>
                    </div>
                ) : null}
            </DialogContent>
        </Dialog>
    );
}

function Stat({ label, value }) {
    return (
        <div className="bg-secondary/50 rounded-lg p-2">
            <div className="text-muted-foreground uppercase text-[10px] tracking-wider">{label}</div>
            <div className="font-semibold mt-0.5">{value}</div>
        </div>
    );
}

function CandidateRow({ candidate, onApply }) {
    return (
        <div className="flex items-center gap-3 p-2.5 hover:bg-secondary/40">
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <Badge
                        variant={candidate.score > 0 ? 'default' : 'secondary'}
                        className={candidate.score > 0 ? 'bg-[#1F9D8A] hover:bg-[#1F9D8A]' : ''}
                    >
                        score {candidate.score}
                    </Badge>
                    <span className="text-[11px] text-muted-foreground">{candidate.count}\u00d7 seen</span>
                </div>
                <code className="text-xs font-mono break-all block mt-1 text-foreground/90">
                    {candidate.url}
                </code>
            </div>
            <Button
                size="sm"
                variant="outline"
                onClick={onApply}
                data-testid={`admin-discover-apply-${candidate.url}`}
            >
                <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Apply
            </Button>
        </div>
    );
}

export default DiscoveryDialog;
